import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Candidate|History|Credential Requests|';
const sharedProperties: Pick<LocDef, 'shared' | 'feature' | 'portal' | 'htmlAllowed'> = {
	shared: false,
	feature: HubFeature.Credential,
	portal: 'SP',
	htmlAllowed: false,
};

type LocNameStart = 'SPCandidateHistoryCredentialRequests';
export type SPCandidateHistoryCredentialRequestsLocNames =
	| `${LocNameStart}TitleTab`
	| `${LocNameStart}TitleManualOverrides`
	| `${LocNameStart}ButtonGrantCredential`
	| `${LocNameStart}ContentManualGrantCredential`
	| `${LocNameStart}ButtonChangeCredential`
	| `${LocNameStart}ContentMakeManualChange`
	| `${LocNameStart}ButtonRemoveChange`
	| `${LocNameStart}ContentRemoveManualChange`
	| `${LocNameStart}ButtonRequestCredentialRebuild`
	| `${LocNameStart}ContentRebuildCredential`
	| `${LocNameStart}ButtonRebuildCandidate`
	| `${LocNameStart}ContentCertificationRebuild`
	| `${LocNameStart}ButtonQueueCpr`
	| `${LocNameStart}ContentQueueCpr`
	| `${LocNameStart}TitleCredentialRequests`
	| `${LocNameStart}LabelCreated`
	| `${LocNameStart}LabelType`
	| `${LocNameStart}LabelStatus`
	| `${LocNameStart}LabelCreator`
	| `${LocNameStart}SuccessMessage`
	| `${LocNameStart}ErrorMessage`;

export const spCandidateHistoryCredentialRequestsLocs: LocDefs<SPCandidateHistoryCredentialRequestsLocNames> =
	{
		SPCandidateHistoryCredentialRequestsTitleTab: {
			defaultValue: 'Credential Requests',
			description: 'Tab Title',
			displayPath: `${pathStart}Tab Title`,
			displayType: 'Title',
			legacyName: 'Credential/CredentialRequests.Title.Tab',
			...sharedProperties,
		},
		SPCandidateHistoryCredentialRequestsTitleManualOverrides: {
			defaultValue: 'Manual Overrides',
			description: 'Manual Overrides Title',
			displayPath: `${pathStart}Manual Overrides Title`,
			displayType: 'Title',
			legacyName: 'Credential/CredentialRequests.Title.ManualOverrides',
			...sharedProperties,
		},
		SPCandidateHistoryCredentialRequestsButtonGrantCredential: {
			defaultValue: 'Grant a Credential',
			description: 'Button to grant a credential',
			displayPath: `${pathStart}Grant Credential`,
			displayType: 'Button',
			legacyName: 'Credential/CredentialRequests.Button.GrantCredential',
			...sharedProperties,
		},
		SPCandidateHistoryCredentialRequestsContentManualGrantCredential: {
			defaultValue: 'Manually grant a credential to the candidate.',
			description: 'Content for manually granting a credential',
			displayPath: `${pathStart}Manual Grant Credential Description`,
			displayType: 'Content',
			legacyName: 'Credential/CredentialRequests.Content.ManualGrantCredential',
			...sharedProperties,
		},
		SPCandidateHistoryCredentialRequestsButtonChangeCredential: {
			defaultValue: 'Change a Credential',
			description: 'Button to change a credential',
			displayPath: `${pathStart}Change Credential`,
			displayType: 'Button',
			legacyName: 'Credential/CredentialRequests.Button.ChangeCredential',
			...sharedProperties,
		},
		SPCandidateHistoryCredentialRequestsContentMakeManualChange: {
			defaultValue: 'Make a manual change to a credential.',
			description: 'Content for making a manual change to a credential',
			displayPath: `${pathStart}Make Manual Change Description`,
			displayType: 'Content',
			legacyName: 'Credential/CredentialRequests.Content.MakeManualChange',
			...sharedProperties,
		},
		SPCandidateHistoryCredentialRequestsButtonRemoveChange: {
			defaultValue: 'Remove a Change',
			description: 'Button to remove a change',
			displayPath: `${pathStart}Remove Change`,
			displayType: 'Button',
			legacyName: 'Credential/CredentialRequests.Button.RemoveChange',
			...sharedProperties,
		},
		SPCandidateHistoryCredentialRequestsContentRemoveManualChange: {
			defaultValue:
				'Remove a manual change made to a credential. The system will rebuild the candidate, omitting that change.',
			description: 'Content for removing a manual change',
			displayPath: `${pathStart}Remove Manual Change Description`,
			displayType: 'Content',
			legacyName: 'Credential/CredentialRequests.Content.RemoveManualChange',
			...sharedProperties,
		},
		SPCandidateHistoryCredentialRequestsButtonRequestCredentialRebuild: {
			defaultValue: 'Request Credential Rebuild',
			description: 'Button to request a credential rebuild',
			displayPath: `${pathStart}Request Credential Rebuild`,
			displayType: 'Button',
			legacyName: 'Credential/CredentialRequests.Button.RequestCredentialRebuild',
			...sharedProperties,
		},
		SPCandidateHistoryCredentialRequestsContentRebuildCredential: {
			defaultValue: 'Rebuild credential data for candidate.',
			description: 'Content for rebuilding credential data',
			displayPath: `${pathStart}Rebuild Credential Description`,
			displayType: 'Content',
			legacyName: 'Credential/CredentialRequests.Content.RebuildCredential',
			...sharedProperties,
		},
		SPCandidateHistoryCredentialRequestsButtonRebuildCandidate: {
			defaultValue: 'Rebuild A Candidate',
			description: 'Button to rebuild a candidate',
			displayPath: `${pathStart}Rebuild Candidate`,
			displayType: 'Button',
			legacyName: 'Credential/CredentialRequests.Button.RebuildCandidate',
			...sharedProperties,
		},
		SPCandidateHistoryCredentialRequestsContentCertificationRebuild: {
			defaultValue:
				"All certification data for this candidate will be removed from the system. The system will then begin with the earliest date a cert, exam, or other requirement was created and rebuild the candidate's certification history up until the present; manual changes will be preserved. This rebuild will take a few minutes.",
			description: 'Content for certification rebuild',
			displayPath: `${pathStart}Certification Rebuild Description`,
			displayType: 'Content',
			legacyName: 'Credential/CredentialRequests.Content.CertificationRebuild',
			...sharedProperties,
		},
		SPCandidateHistoryCredentialRequestsButtonQueueCpr: {
			defaultValue: 'Queue Credential Process Request',
			description: 'Button to queue a credential process request',
			displayPath: `${pathStart}Queue Cpr`,
			displayType: 'Button',
			legacyName: 'Credential/CredentialRequests.Button.QueueCpr',
			...sharedProperties,
		},
		SPCandidateHistoryCredentialRequestsContentQueueCpr: {
			defaultValue:
				"Attempt to queue a certification process request; if another request has been made less than a minute ago (max = 1/minute), it will be ignored. The list at the bottom of this page shows information about recent requests. Be aware that when a candidate logs in, and when you view the candidate's summary page, a similar request attempt is made (max = 1/hour).",
			description: 'Content for queuing a credential process request',
			displayPath: `${pathStart}Queue Cpr Description`,
			displayType: 'Content',
			legacyName: 'Credential/CredentialRequests.Content.QueueCpr',
			...sharedProperties,
		},
		SPCandidateHistoryCredentialRequestsTitleCredentialRequests: {
			defaultValue: 'Credential Requests',
			description: 'Title for credential requests section',
			displayPath: `${pathStart}Credential Requests Title`,
			displayType: 'Title',
			legacyName: 'Credential/CredentialRequests.Title.CredentialRequests',
			...sharedProperties,
		},
		SPCandidateHistoryCredentialRequestsLabelCreated: {
			defaultValue: 'When',
			description: 'Label for: When',
			displayPath: `${pathStart}When`,
			displayType: 'Label',
			legacyName: 'Credential/CredentialRequests.ColumnHeading.When',
			...sharedProperties,
		},
		SPCandidateHistoryCredentialRequestsLabelType: {
			defaultValue: 'Type',
			description: 'Label for: Type',
			displayPath: `${pathStart}Type`,
			displayType: 'Label',
			legacyName: 'Credential/CredentialRequests.ColumnHeading.Type',
			...sharedProperties,
		},
		SPCandidateHistoryCredentialRequestsLabelStatus: {
			defaultValue: 'Status',
			description: 'Label for: Status',
			displayPath: `${pathStart}Status`,
			displayType: 'Label',
			legacyName: 'Credential/CredentialRequests.ColumnHeading.Status',
			...sharedProperties,
		},
		SPCandidateHistoryCredentialRequestsLabelCreator: {
			defaultValue: 'Creator',
			description: 'Label for: Creator',
			displayPath: `${pathStart}Creator`,
			displayType: 'Label',
			legacyName: 'Credential/CredentialRequests.ColumnHeading.Creator',
			...sharedProperties,
		},
		SPCandidateHistoryCredentialRequestsSuccessMessage: {
			defaultValue: 'Your request has successfully been submitted.',
			description: 'Success message for credential request submission',
			displayPath: `${pathStart}Success Message`,
			displayType: 'Content',
			legacyName: 'Credential/CredentialRequests.NotificationMessage.Success',
			...sharedProperties,
		},
		SPCandidateHistoryCredentialRequestsErrorMessage: {
			defaultValue: 'There was an error submitting your request.',
			description: 'Error message for credential request submission',
			displayPath: `${pathStart}Error Message`,
			displayType: 'Content',
			legacyName: 'Credential/CredentialRequests.NotificationMessage.Error',
			...sharedProperties,
		},
	};

export type SPCandidateHistoryCredentialRequestsLocType =
	LocMap<SPCandidateHistoryCredentialRequestsLocNames>;
